import type {
  ActivityLog,
  ApiFailure,
  ApiSuccess,
  AuthResponse,
  AuthUser,
  LifecycleAction,
  ManagedUser,
  PaginatedActivityLogs,
  PaginatedUsers,
  RegisterStudentInput,
  RegisterTeacherInput,
  RoleChangeRequest,
  RoleChangeRequestStatus,
  UserRole,
  UserStatus
} from '../types/auth';
import type {
  CreateClassInput,
  SchoolClass,
  UpdateClassInput
} from '../types/class';
import type {
  ClassActivityAssignment,
  ClassContentType,
  ReplaceClassActivityAssignmentsInput
} from '../types/classActivityAssignment';
import type {
  AttachLectureLinkInput,
  CharacterPillar,
  CreateCharacterPillarInput,
  CreateCharacterPillarResult,
  CreateHomeActivityInput,
  CreateLearningLectureInput,
  CreateMissionInput,
  ContentFileRef,
  ContentLinkRef,
  HomeActivity,
  LearningLecture,
  Mission,
  PresignContentInput,
  PresignContentResult,
  UpdateCharacterPillarInput,
  UpdateHomeActivityInput,
  UpdateLearningLectureInput,
  UpdateMissionInput,
  CharacterContentFileType
} from '../types/characterPillar';
import type {
  CreateRegistrationFormInput,
  PublicRegistrationForm,
  RegistrationForm,
  RegistrationFormWorkflowSteps,
  RegistrationSubmission,
  RegistrationSubmissionStatus,
  AssignRegistrationClassInput,
  SubmitRegistrationInput,
  UpdateRegistrationFormInput
} from '../types/registration';
import type {
  RegisterSchoolInput,
  RegisterSchoolResult,
  School,
  SchoolLifecycleAction,
  SchoolStatus,
  UpdateSchoolInput
} from '../types/school';
import type { Badge, CreateBadgeInput, UpdateBadgeInput } from '../types/badge';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v1';

const ACCESS_TOKEN_KEY = 'tfm_admin_access_token';
const REFRESH_TOKEN_KEY = 'tfm_admin_refresh_token';

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

async function parseResponse<T>(response: Response): Promise<T> {
  const payload = (await response.json()) as ApiSuccess<T> | ApiFailure;

  if (!response.ok || !payload.success) {
    const base =
      !payload.success && payload.message
        ? payload.message
        : `Request failed with status ${response.status}`;
    const details =
      !payload.success && payload.errors?.length
        ? payload.errors.map((e) => e.message).join('; ')
        : null;
    throw new Error(details ? `${base}: ${details}` : base);
  }

  return payload.data;
}

const ADMIN_DEVICE_KEY = 'tfm_admin_device_id';

function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(ADMIN_DEVICE_KEY);
  if (existing) return existing;
  const deviceId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `admin-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(ADMIN_DEVICE_KEY, deviceId);
  return deviceId;
}

type NavigatorUAData = {
  brands?: Array<{ brand: string; version: string }>;
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<{
    model?: string;
    platform?: string;
    platformVersion?: string;
  }>;
};

function getClientUserAgent(): string {
  return typeof navigator !== 'undefined' ? navigator.userAgent : '';
}

function parseDeviceNameFromUserAgent(ua: string): string {
  const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  let os = 'Unknown OS';
  if (/Windows NT/i.test(ua)) os = 'Windows';
  else if (/Mac OS X/i.test(ua)) os = 'macOS';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
  else if (/Linux/i.test(ua)) os = 'Linux';
  else if (/CrOS/i.test(ua)) os = 'Chrome OS';

  let browser = 'Browser';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';
  else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = 'Safari';

  return isMobile ? `${browser} on ${os} (mobile)` : `${browser} on ${os}`;
}

/** Best-effort device label from Client Hints, then UA parsing. Never hardcoded. */
async function resolveDeviceName(): Promise<string> {
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
  if (uaData?.getHighEntropyValues) {
    try {
      const hints = await uaData.getHighEntropyValues(['model', 'platform', 'platformVersion']);
      if (hints.model && hints.model.trim()) {
        return hints.model.trim();
      }
      if (hints.platform) {
        const brand = uaData.brands?.find((b) => !/Not.?A.?Brand/i.test(b.brand))?.brand;
        const platformLabel = hints.platformVersion
          ? `${hints.platform} ${hints.platformVersion}`
          : hints.platform;
        return brand ? `${brand} on ${platformLabel}` : platformLabel;
      }
    } catch {
      // Client Hints unavailable — fall through to UA parse.
    }
  }

  if (uaData?.platform) {
    const brand = uaData.brands?.find((b) => !/Not.?A.?Brand/i.test(b.brand))?.brand;
    return brand ? `${brand} on ${uaData.platform}` : uaData.platform;
  }

  return parseDeviceNameFromUserAgent(getClientUserAgent());
}

function authHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

function toId(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && '$oid' in value) {
    return String((value as { $oid: string }).$oid);
  }
  return value != null ? String(value) : '';
}

function normalizeUser(
  user: AuthUser & {
    _id?: string;
    fullName: string;
    username?: string;
    isActive?: boolean;
    schoolId?: string | null;
    createdAt?: string;
    updatedAt?: string;
  }
): ManagedUser {
  return {
    id: user.id || toId(user._id),
    fullName: user.fullName,
    email: user.email,
    username: user.username || '',
    role: user.role,
    status: user.status,
    isActive: user.isActive ?? user.status === 'active',
    schoolId: user.schoolId ? toId(user.schoolId) : null,
    createdAt: user.createdAt || '',
    updatedAt: user.updatedAt || ''
  };
}

function normalizeAuthUser(
  user: AuthUser & { _id?: string; fullName: string; schoolId?: string | null }
): AuthUser {
  return {
    id: user.id || toId(user._id),
    fullName: user.fullName,
    email: user.email,
    role: user.role,
    status: user.status,
    schoolId: user.schoolId ? toId(user.schoolId) : null
  };
}

function normalizeRoleChangeRequest(raw: Record<string, unknown>): RoleChangeRequest {
  return {
    id: toId(raw._id ?? raw.id),
    requesterUserId: toId(raw.requesterUserId),
    targetUserId: toId(raw.targetUserId),
    currentRole: raw.currentRole as UserRole,
    requestedRole: raw.requestedRole as UserRole,
    schoolId: raw.schoolId ? toId(raw.schoolId) : null,
    reason: typeof raw.reason === 'string' ? raw.reason : undefined,
    status: raw.status as RoleChangeRequestStatus,
    reviewNote: typeof raw.reviewNote === 'string' ? raw.reviewNote : undefined,
    reviewedBy: raw.reviewedBy ? toId(raw.reviewedBy) : null,
    reviewedAt: typeof raw.reviewedAt === 'string' ? raw.reviewedAt : null,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : ''
  };
}

function normalizeActivityLog(raw: Record<string, unknown>): ActivityLog {
  return {
    id: toId(raw._id ?? raw.id),
    actorUserId: raw.actorUserId ? toId(raw.actorUserId) : null,
    action: String(raw.action ?? ''),
    resourceType: typeof raw.resourceType === 'string' ? raw.resourceType : undefined,
    resourceId: typeof raw.resourceId === 'string' ? raw.resourceId : undefined,
    metadata:
      raw.metadata && typeof raw.metadata === 'object'
        ? (raw.metadata as Record<string, unknown>)
        : undefined,
    ipAddress: typeof raw.ipAddress === 'string' ? raw.ipAddress : undefined,
    userAgent: typeof raw.userAgent === 'string' ? raw.userAgent : undefined,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : ''
  };
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export async function login(username: string, password: string): Promise<AuthResponse> {
  const deviceName = await resolveDeviceName();
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      username,
      password,
      deviceId: getOrCreateDeviceId(),
      userAgent: getClientUserAgent(),
      deviceName
    })
  });

  const data = await parseResponse<AuthResponse & { user: AuthUser & { _id?: string } }>(response);
  return {
    ...data,
    user: normalizeAuthUser(data.user)
  };
}

export async function getMyProfile(): Promise<AuthUser> {
  const response = await fetch(`${API_BASE_URL}/users/me`, {
    headers: authHeaders()
  });

  const user = await parseResponse<AuthUser & { _id?: string; fullName: string }>(response);
  return normalizeAuthUser(user);
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ currentPassword, newPassword })
  });

  await parseResponse(response);
}

export async function listUsers(filters: {
  role?: UserRole | '';
  status?: UserStatus | '';
  schoolId?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<PaginatedUsers> {
  const query = buildQuery({
    role: filters.role || undefined,
    status: filters.status || undefined,
    schoolId: filters.schoolId || undefined,
    search: filters.search || undefined,
    page: filters.page ?? 1,
    limit: filters.limit ?? 25
  });

  const response = await fetch(`${API_BASE_URL}/users${query}`, {
    headers: authHeaders()
  });

  const data = await parseResponse<{
    items: Array<ManagedUser & { _id?: string }>;
    total: number;
    page: number;
    limit: number;
  }>(response);

  return {
    items: data.items.map((item) => normalizeUser(item)),
    total: data.total,
    page: data.page,
    limit: data.limit
  };
}

export async function applyLifecycleAction(
  userId: string,
  action: LifecycleAction,
  reason?: string
): Promise<ManagedUser> {
  const response = await fetch(`${API_BASE_URL}/users/${userId}/${action}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(reason ? { reason } : {})
  });

  const user = await parseResponse<ManagedUser & { _id?: string }>(response);
  return normalizeUser(user);
}

export async function listRoleChangeRequests(
  status?: RoleChangeRequestStatus | ''
): Promise<RoleChangeRequest[]> {
  const query = buildQuery({ status: status || undefined, page: 1, limit: 100 });
  const response = await fetch(`${API_BASE_URL}/role-change-requests${query}`, {
    headers: authHeaders()
  });

  const data = await parseResponse<{
    items: Array<Record<string, unknown>>;
    total: number;
    page: number;
    limit: number;
  }>(response);
  return data.items.map(normalizeRoleChangeRequest);
}

export async function reviewRoleChangeRequest(
  requestId: string,
  decision: 'approve' | 'reject',
  reviewNote?: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/role-change-requests/${requestId}/${decision}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(reviewNote ? { reviewNote } : {})
  });

  await parseResponse(response);
}

export async function listActivityLogs(filters: {
  action?: string;
  page?: number;
  limit?: number;
}): Promise<PaginatedActivityLogs> {
  const query = buildQuery({
    action: filters.action || undefined,
    page: filters.page ?? 1,
    limit: filters.limit ?? 25
  });

  const response = await fetch(`${API_BASE_URL}/activity-logs${query}`, {
    headers: authHeaders()
  });

  const data = await parseResponse<{
    items: Array<Record<string, unknown>>;
    total: number;
    page: number;
    limit: number;
  }>(response);

  return {
    items: data.items.map(normalizeActivityLog),
    total: data.total,
    page: data.page,
    limit: data.limit
  };
}

function normalizeSchool(raw: Record<string, unknown>): School {
  const address =
    raw.address && typeof raw.address === 'object'
      ? (raw.address as School['address'])
      : null;
  const contactInfo =
    raw.contactInfo && typeof raw.contactInfo === 'object'
      ? (raw.contactInfo as School['contactInfo'])
      : null;

  return {
    id: toId(raw._id ?? raw.id),
    name: String(raw.name ?? ''),
    shortName: String(raw.shortName ?? ''),
    address,
    contactInfo,
    isActive: Boolean(raw.isActive),
    adminUserId: toId(raw.adminUserId),
    status: raw.status as SchoolStatus,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : ''
  };
}

export async function listSchools(filters?: {
  status?: SchoolStatus | '';
  isActive?: boolean | '';
}): Promise<School[]> {
  const query = buildQuery({
    status: filters?.status || undefined,
    isActive:
      filters?.isActive === true
        ? 'true'
        : filters?.isActive === false
          ? 'false'
          : undefined
  });

  const response = await fetch(`${API_BASE_URL}/schools${query}`, {
    headers: authHeaders()
  });

  const data = await parseResponse<Array<Record<string, unknown>>>(response);
  return data.map(normalizeSchool);
}

export async function getSchool(schoolId: string): Promise<School> {
  const response = await fetch(`${API_BASE_URL}/schools/${schoolId}`, {
    headers: authHeaders()
  });

  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeSchool(data);
}

export async function updateSchool(
  schoolId: string,
  body: UpdateSchoolInput
): Promise<School> {
  const response = await fetch(`${API_BASE_URL}/schools/${schoolId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });

  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeSchool(data);
}

export async function applySchoolLifecycleAction(
  schoolId: string,
  action: SchoolLifecycleAction,
  reason?: string
): Promise<School> {
  const response = await fetch(`${API_BASE_URL}/schools/${schoolId}/${action}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(reason ? { reason } : {})
  });

  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeSchool(data);
}

/** Registers SCHOOL_ADMIN credentials + school bootstrap (pending until admin is activated). */
export async function registerSchool(
  input: RegisterSchoolInput
): Promise<RegisterSchoolResult> {
  const response = await fetch(`${API_BASE_URL}/auth/register/school`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input)
  });

  return parseResponse<RegisterSchoolResult>(response);
}

/**
 * Approves a pending school by activating its SCHOOL_ADMIN.
 * school-service listens for UserActivated and sets school status=approved.
 */
export async function approveSchoolRegistration(adminUserId: string): Promise<ManagedUser> {
  return applyLifecycleAction(adminUserId, 'activate');
}

export async function registerTeacher(input: RegisterTeacherInput): Promise<{ user: ManagedUser }> {
  const response = await fetch(`${API_BASE_URL}/auth/register/teacher`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input)
  });

  const data = await parseResponse<{ user: ManagedUser & { _id?: string } }>(response);
  return { user: normalizeUser(data.user) };
}

export async function registerStudent(input: RegisterStudentInput): Promise<{ user: ManagedUser }> {
  const response = await fetch(`${API_BASE_URL}/auth/register/student`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input)
  });

  const data = await parseResponse<{ user: ManagedUser & { _id?: string } }>(response);
  return { user: normalizeUser(data.user) };
}

function normalizeClass(raw: Record<string, unknown>): SchoolClass {
  const studentUserIds = Array.isArray(raw.studentUserIds)
    ? raw.studentUserIds.map((id) => toId(id))
    : [];

  return {
    id: toId(raw._id ?? raw.id),
    name: String(raw.name ?? ''),
    schoolId: toId(raw.schoolId),
    grade: typeof raw.grade === 'string' ? raw.grade : raw.grade === null ? null : undefined,
    teacherUserId: raw.teacherUserId ? toId(raw.teacherUserId) : null,
    studentUserIds,
    isActive: Boolean(raw.isActive),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : ''
  };
}

export async function listClasses(
  schoolId: string,
  filters?: { activeOnly?: boolean }
): Promise<SchoolClass[]> {
  const query = buildQuery({
    activeOnly: filters?.activeOnly === true ? 'true' : undefined
  });
  const response = await fetch(`${API_BASE_URL}/schools/${schoolId}/classes${query}`, {
    headers: authHeaders()
  });
  const data = await parseResponse<Array<Record<string, unknown>>>(response);
  return data.map(normalizeClass);
}

export async function getClass(schoolId: string, classId: string): Promise<SchoolClass> {
  const response = await fetch(`${API_BASE_URL}/schools/${schoolId}/classes/${classId}`, {
    headers: authHeaders()
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeClass(data);
}

export async function createClass(
  schoolId: string,
  body: CreateClassInput
): Promise<SchoolClass> {
  const response = await fetch(`${API_BASE_URL}/schools/${schoolId}/classes`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeClass(data);
}

export async function updateClass(
  schoolId: string,
  classId: string,
  body: UpdateClassInput
): Promise<SchoolClass> {
  const response = await fetch(`${API_BASE_URL}/schools/${schoolId}/classes/${classId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeClass(data);
}

export async function assignClassTeacher(
  schoolId: string,
  classId: string,
  teacherUserId: string | null
): Promise<SchoolClass> {
  const response = await fetch(`${API_BASE_URL}/schools/${schoolId}/classes/${classId}/teacher`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ teacherUserId })
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeClass(data);
}

export async function setClassStudents(
  schoolId: string,
  classId: string,
  studentUserIds: string[]
): Promise<SchoolClass> {
  const response = await fetch(`${API_BASE_URL}/schools/${schoolId}/classes/${classId}/students`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ studentUserIds })
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeClass(data);
}

export async function deactivateClass(schoolId: string, classId: string): Promise<SchoolClass> {
  const response = await fetch(
    `${API_BASE_URL}/schools/${schoolId}/classes/${classId}/deactivate`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeClass(data);
}

export async function reactivateClass(schoolId: string, classId: string): Promise<SchoolClass> {
  const response = await fetch(
    `${API_BASE_URL}/schools/${schoolId}/classes/${classId}/reactivate`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeClass(data);
}

function normalizeClassActivityAssignment(
  raw: Record<string, unknown>
): ClassActivityAssignment {
  return {
    id: toId(raw._id ?? raw.id),
    schoolId: toId(raw.schoolId),
    classId: toId(raw.classId),
    contentType: String(raw.contentType) as ClassContentType,
    contentId: toId(raw.contentId),
    characterPillarId: toId(raw.characterPillarId),
    isActive: Boolean(raw.isActive),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : ''
  };
}

export async function listClassActivityAssignments(filters: {
  classId?: string;
  schoolId?: string;
  activeOnly?: boolean;
}): Promise<ClassActivityAssignment[]> {
  const query = buildQuery({
    classId: filters.classId,
    schoolId: filters.schoolId,
    activeOnly: filters.activeOnly === true ? 'true' : undefined
  });
  const response = await fetch(`${API_BASE_URL}/class-activity-assignments${query}`, {
    headers: authHeaders()
  });
  const data = await parseResponse<Array<Record<string, unknown>>>(response);
  return data.map(normalizeClassActivityAssignment);
}

export async function replaceClassActivityAssignments(
  classId: string,
  body: ReplaceClassActivityAssignmentsInput
): Promise<ClassActivityAssignment[]> {
  const response = await fetch(
    `${API_BASE_URL}/class-activity-assignments/classes/${classId}`,
    {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Array<Record<string, unknown>>>(response);
  return data.map(normalizeClassActivityAssignment);
}

function normalizeWorkflowSteps(raw: unknown): RegistrationFormWorkflowSteps {
  const steps = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    submissionReview: steps.submissionReview !== false,
    interview: steps.interview !== false
  };
}

function normalizeRegistrationForm(raw: Record<string, unknown>): RegistrationForm {
  const fields = Array.isArray(raw.fields)
    ? raw.fields.map((field, index) => {
        const item = field as Record<string, unknown>;
        return {
          key: String(item.key ?? ''),
          label: String(item.label ?? ''),
          type: item.type as RegistrationForm['fields'][number]['type'],
          required: Boolean(item.required),
          options: Array.isArray(item.options)
            ? item.options.map((option) => String(option))
            : undefined,
          order: typeof item.order === 'number' ? item.order : index
        };
      })
    : [];

  return {
    id: toId(raw._id ?? raw.id),
    title: String(raw.title ?? ''),
    description:
      typeof raw.description === 'string'
        ? raw.description
        : raw.description === null
          ? null
          : undefined,
    schoolId: toId(raw.schoolId),
    fields,
    workflowSteps: normalizeWorkflowSteps(raw.workflowSteps),
    autoEnrollStudent: Boolean(raw.autoEnrollStudent),
    autoEnrollClassId: raw.autoEnrollClassId ? toId(raw.autoEnrollClassId) : null,
    isActive: Boolean(raw.isActive),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : ''
  };
}

export async function listRegistrationForms(
  schoolId: string,
  filters?: { activeOnly?: boolean }
): Promise<RegistrationForm[]> {
  const query = buildQuery({
    activeOnly: filters?.activeOnly === true ? 'true' : undefined
  });
  const response = await fetch(
    `${API_BASE_URL}/schools/${schoolId}/registration-forms${query}`,
    { headers: authHeaders() }
  );
  const data = await parseResponse<Array<Record<string, unknown>>>(response);
  return data.map(normalizeRegistrationForm);
}

export async function createRegistrationForm(
  schoolId: string,
  body: CreateRegistrationFormInput
): Promise<RegistrationForm> {
  const response = await fetch(`${API_BASE_URL}/schools/${schoolId}/registration-forms`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeRegistrationForm(data);
}

export async function updateRegistrationForm(
  schoolId: string,
  formId: string,
  body: UpdateRegistrationFormInput
): Promise<RegistrationForm> {
  const response = await fetch(
    `${API_BASE_URL}/schools/${schoolId}/registration-forms/${formId}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeRegistrationForm(data);
}

export async function deleteRegistrationForm(
  schoolId: string,
  formId: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/schools/${schoolId}/registration-forms/${formId}`,
    {
      method: 'DELETE',
      headers: authHeaders()
    }
  );
  await parseResponse<Record<string, unknown> | null>(response);
}

export async function closeRegistrationForm(
  schoolId: string,
  formId: string
): Promise<RegistrationForm> {
  const response = await fetch(
    `${API_BASE_URL}/schools/${schoolId}/registration-forms/${formId}/close`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeRegistrationForm(data);
}

function optionalText(value: unknown): string | null | undefined {
  if (typeof value === 'string') return value;
  if (value === null) return null;
  return undefined;
}

function normalizeContentFiles(raw: unknown): ContentFileRef[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const file = item as Record<string, unknown>;
    return {
      fileId: String(file.fileId ?? ''),
      originalFilename: String(file.originalFilename ?? ''),
      mimeType: String(file.mimeType ?? ''),
      size: typeof file.size === 'number' ? file.size : 0,
      s3Key: String(file.s3Key ?? ''),
      bucket: String(file.bucket ?? ''),
      fileType: file.fileType as ContentFileRef['fileType']
    };
  });
}

function normalizeCharacterPillar(raw: Record<string, unknown>): CharacterPillar {
  const colorTheme =
    typeof raw.colorTheme === 'string' && raw.colorTheme.trim()
      ? raw.colorTheme.trim()
      : '#888888';
  const icon = typeof raw.icon === 'string' ? raw.icon.trim() : '';
  return {
    id: toId(raw._id ?? raw.id),
    name: String(raw.name ?? ''),
    description: optionalText(raw.description),
    colorTheme,
    icon,
    sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : 0,
    isActive: Boolean(raw.isActive),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : ''
  };
}

function normalizeMission(raw: Record<string, unknown>): Mission {
  return {
    id: toId(raw._id ?? raw.id),
    name: String(raw.name ?? ''),
    description: optionalText(raw.description),
    instructions: optionalText(raw.instructions),
    characterPillarId: toId(raw.characterPillarId),
    xp: typeof raw.xp === 'number' && Number.isFinite(raw.xp) ? raw.xp : 0,
    badgeId: raw.badgeId ? toId(raw.badgeId) : null,
    sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : 0,
    isActive: Boolean(raw.isActive),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : ''
  };
}

function normalizeContentLinks(raw: unknown): ContentLinkRef[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const link = item as Record<string, unknown>;
    return {
      linkId: String(link.linkId ?? ''),
      url: String(link.url ?? ''),
      label: typeof link.label === 'string' ? link.label : undefined,
      linkType: (link.linkType as ContentLinkRef['linkType']) || 'video'
    };
  });
}

function normalizeLearningLecture(raw: Record<string, unknown>): LearningLecture {
  return {
    id: toId(raw._id ?? raw.id),
    title: String(raw.title ?? ''),
    description: optionalText(raw.description),
    characterPillarId: toId(raw.characterPillarId),
    contentFiles: normalizeContentFiles(raw.contentFiles),
    contentLinks: normalizeContentLinks(raw.contentLinks),
    xp: typeof raw.xp === 'number' && Number.isFinite(raw.xp) ? raw.xp : 0,
    badgeId: raw.badgeId ? toId(raw.badgeId) : null,
    sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : 0,
    isActive: Boolean(raw.isActive),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : ''
  };
}

function normalizeHomeActivity(raw: Record<string, unknown>): HomeActivity {
  return {
    id: toId(raw._id ?? raw.id),
    title: String(raw.title ?? ''),
    description: optionalText(raw.description),
    instructions: optionalText(raw.instructions),
    characterPillarId: toId(raw.characterPillarId),
    contentFiles: normalizeContentFiles(raw.contentFiles),
    xp: typeof raw.xp === 'number' && Number.isFinite(raw.xp) ? raw.xp : 0,
    badgeId: raw.badgeId ? toId(raw.badgeId) : null,
    sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : 0,
    isActive: Boolean(raw.isActive),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : ''
  };
}

export async function listCharacterPillars(filters?: {
  activeOnly?: boolean;
}): Promise<CharacterPillar[]> {
  const query = buildQuery({
    activeOnly: filters?.activeOnly === true ? 'true' : undefined
  });
  const response = await fetch(`${API_BASE_URL}/character-pillars${query}`, {
    headers: authHeaders()
  });
  const data = await parseResponse<Array<Record<string, unknown>>>(response);
  return data.map(normalizeCharacterPillar);
}

export async function getCharacterPillar(characterPillarId: string): Promise<CharacterPillar> {
  const response = await fetch(`${API_BASE_URL}/character-pillars/${characterPillarId}`, {
    headers: authHeaders()
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeCharacterPillar(data);
}

export async function createCharacterPillar(
  body: CreateCharacterPillarInput
): Promise<CreateCharacterPillarResult> {
  const response = await fetch(`${API_BASE_URL}/character-pillars`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  const pillarRaw = (data.pillar as Record<string, unknown> | undefined) ?? data;
  return {
    pillar: normalizeCharacterPillar(pillarRaw),
    missions: Array.isArray(data.missions)
      ? data.missions.map((item) => normalizeMission(item as Record<string, unknown>))
      : [],
    learningLectures: Array.isArray(data.learningLectures)
      ? data.learningLectures.map((item) =>
          normalizeLearningLecture(item as Record<string, unknown>)
        )
      : [],
    homeActivities: Array.isArray(data.homeActivities)
      ? data.homeActivities.map((item) => normalizeHomeActivity(item as Record<string, unknown>))
      : []
  };
}

export async function updateCharacterPillar(
  characterPillarId: string,
  body: UpdateCharacterPillarInput
): Promise<CharacterPillar> {
  const response = await fetch(`${API_BASE_URL}/character-pillars/${characterPillarId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeCharacterPillar(data);
}

export async function deactivateCharacterPillar(
  characterPillarId: string
): Promise<CharacterPillar> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/deactivate`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeCharacterPillar(data);
}

export async function reactivateCharacterPillar(
  characterPillarId: string
): Promise<CharacterPillar> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/reactivate`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeCharacterPillar(data);
}

export async function listMissions(
  characterPillarId: string,
  filters?: { activeOnly?: boolean }
): Promise<Mission[]> {
  const query = buildQuery({
    activeOnly: filters?.activeOnly === true ? 'true' : undefined
  });
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/missions${query}`,
    { headers: authHeaders() }
  );
  const data = await parseResponse<Array<Record<string, unknown>>>(response);
  return data.map(normalizeMission);
}

export async function createMission(
  characterPillarId: string,
  body: CreateMissionInput
): Promise<Mission> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/missions`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeMission(data);
}

export async function updateMission(
  characterPillarId: string,
  missionId: string,
  body: UpdateMissionInput
): Promise<Mission> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/missions/${missionId}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeMission(data);
}

export async function deactivateMission(
  characterPillarId: string,
  missionId: string
): Promise<Mission> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/missions/${missionId}/deactivate`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeMission(data);
}

export async function reactivateMission(
  characterPillarId: string,
  missionId: string
): Promise<Mission> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/missions/${missionId}/reactivate`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeMission(data);
}

export async function listLearningLectures(
  characterPillarId: string,
  filters?: { activeOnly?: boolean }
): Promise<LearningLecture[]> {
  const query = buildQuery({
    activeOnly: filters?.activeOnly === true ? 'true' : undefined
  });
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/learning-lectures${query}`,
    { headers: authHeaders() }
  );
  const data = await parseResponse<Array<Record<string, unknown>>>(response);
  return data.map(normalizeLearningLecture);
}

export async function createLearningLecture(
  characterPillarId: string,
  body: CreateLearningLectureInput
): Promise<LearningLecture> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/learning-lectures`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeLearningLecture(data);
}

export async function updateLearningLecture(
  characterPillarId: string,
  lectureId: string,
  body: UpdateLearningLectureInput
): Promise<LearningLecture> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/learning-lectures/${lectureId}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeLearningLecture(data);
}

export async function deactivateLearningLecture(
  characterPillarId: string,
  lectureId: string
): Promise<LearningLecture> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/learning-lectures/${lectureId}/deactivate`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeLearningLecture(data);
}

export async function reactivateLearningLecture(
  characterPillarId: string,
  lectureId: string
): Promise<LearningLecture> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/learning-lectures/${lectureId}/reactivate`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeLearningLecture(data);
}

export async function listHomeActivities(
  characterPillarId: string,
  filters?: { activeOnly?: boolean }
): Promise<HomeActivity[]> {
  const query = buildQuery({
    activeOnly: filters?.activeOnly === true ? 'true' : undefined
  });
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/home-activities${query}`,
    { headers: authHeaders() }
  );
  const data = await parseResponse<Array<Record<string, unknown>>>(response);
  return data.map(normalizeHomeActivity);
}

export async function createHomeActivity(
  characterPillarId: string,
  body: CreateHomeActivityInput
): Promise<HomeActivity> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/home-activities`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeHomeActivity(data);
}

export async function updateHomeActivity(
  characterPillarId: string,
  homeActivityId: string,
  body: UpdateHomeActivityInput
): Promise<HomeActivity> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/home-activities/${homeActivityId}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeHomeActivity(data);
}

export async function deactivateHomeActivity(
  characterPillarId: string,
  homeActivityId: string
): Promise<HomeActivity> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/home-activities/${homeActivityId}/deactivate`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeHomeActivity(data);
}

export async function reactivateHomeActivity(
  characterPillarId: string,
  homeActivityId: string
): Promise<HomeActivity> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/home-activities/${homeActivityId}/reactivate`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeHomeActivity(data);
}

export async function presignLectureContent(
  characterPillarId: string,
  lectureId: string,
  body: PresignContentInput
): Promise<PresignContentResult> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/learning-lectures/${lectureId}/content/presign`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  return parseResponse<PresignContentResult>(response);
}

export async function attachLectureContent(
  characterPillarId: string,
  lectureId: string,
  body: ContentFileRef
): Promise<LearningLecture> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/learning-lectures/${lectureId}/content`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeLearningLecture(data);
}

export async function removeLectureContent(
  characterPillarId: string,
  lectureId: string,
  fileId: string
): Promise<LearningLecture> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/learning-lectures/${lectureId}/content/${fileId}`,
    {
      method: 'DELETE',
      headers: authHeaders()
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeLearningLecture(data);
}

function inferClientFileType(
  mimeType: string,
  filename: string
): CharacterContentFileType {
  const mime = mimeType.toLowerCase();
  const lower = filename.toLowerCase();
  if (mime.startsWith('video/') || /\.(mp4|webm|mov|avi|mkv)$/.test(lower)) return 'video';
  if (mime.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|aac|flac|oga)$/.test(lower)) return 'audio';
  if (mime.startsWith('image/') || /\.(jpe?g|png|webp|gif)$/.test(lower)) return 'image';
  if (
    mime.includes('pdf') ||
    mime.includes('word') ||
    mime.includes('presentation') ||
    mime.includes('sheet') ||
    mime.startsWith('text/') ||
    /\.(pdf|docx?|pptx?|xlsx?|txt|csv|rtf)$/.test(lower)
  ) {
    return 'document';
  }
  return 'other';
}

/** Presign → PUT to S3 → attach metadata on the lecture. */
export async function uploadLectureFile(
  characterPillarId: string,
  lectureId: string,
  file: File
): Promise<LearningLecture> {
  const mimeType = file.type || 'application/octet-stream';
  const fileType = inferClientFileType(mimeType, file.name);
  const presign = await presignLectureContent(characterPillarId, lectureId, {
    originalFilename: file.name,
    mimeType,
    fileType,
    size: file.size
  });

  const uploadResponse = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: {
      ...(presign.headers || {}),
      'Content-Type': mimeType
    },
    body: file
  });
  if (!uploadResponse.ok) {
    throw new Error(`S3 upload failed (${uploadResponse.status}) for ${file.name}`);
  }

  return attachLectureContent(characterPillarId, lectureId, {
    fileId: presign.fileId,
    originalFilename: file.name,
    mimeType,
    size: file.size,
    s3Key: presign.s3Key,
    bucket: presign.bucket,
    fileType
  });
}

export async function attachLectureLink(
  characterPillarId: string,
  lectureId: string,
  body: AttachLectureLinkInput
): Promise<LearningLecture> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/learning-lectures/${lectureId}/links`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeLearningLecture(data);
}

export async function removeLectureLink(
  characterPillarId: string,
  lectureId: string,
  linkId: string
): Promise<LearningLecture> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/learning-lectures/${lectureId}/links/${linkId}`,
    {
      method: 'DELETE',
      headers: authHeaders()
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeLearningLecture(data);
}

export async function presignHomeActivityContent(
  characterPillarId: string,
  homeActivityId: string,
  body: PresignContentInput
): Promise<PresignContentResult> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/home-activities/${homeActivityId}/content/presign`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  return parseResponse<PresignContentResult>(response);
}

export async function attachHomeActivityContent(
  characterPillarId: string,
  homeActivityId: string,
  body: ContentFileRef
): Promise<HomeActivity> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/home-activities/${homeActivityId}/content`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeHomeActivity(data);
}

export async function removeHomeActivityContent(
  characterPillarId: string,
  homeActivityId: string,
  fileId: string
): Promise<HomeActivity> {
  const response = await fetch(
    `${API_BASE_URL}/character-pillars/${characterPillarId}/home-activities/${homeActivityId}/content/${fileId}`,
    {
      method: 'DELETE',
      headers: authHeaders()
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeHomeActivity(data);
}

/** Presign → PUT to S3 → attach metadata on the home activity. */
export async function uploadHomeActivityFile(
  characterPillarId: string,
  homeActivityId: string,
  file: File
): Promise<HomeActivity> {
  const mimeType = file.type || 'application/octet-stream';
  const fileType = inferClientFileType(mimeType, file.name);
  const presign = await presignHomeActivityContent(characterPillarId, homeActivityId, {
    originalFilename: file.name,
    mimeType,
    fileType,
    size: file.size
  });

  const uploadResponse = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: {
      ...(presign.headers || {}),
      'Content-Type': mimeType
    },
    body: file
  });
  if (!uploadResponse.ok) {
    throw new Error(`S3 upload failed (${uploadResponse.status}) for ${file.name}`);
  }

  return attachHomeActivityContent(characterPillarId, homeActivityId, {
    fileId: presign.fileId,
    originalFilename: file.name,
    mimeType,
    size: file.size,
    s3Key: presign.s3Key,
    bucket: presign.bucket,
    fileType
  });
}

function normalizePublicRegistrationForm(raw: Record<string, unknown>): PublicRegistrationForm {
  const form = normalizeRegistrationForm(raw);
  return {
    id: form.id,
    title: form.title,
    description: form.description,
    schoolId: form.schoolId,
    fields: form.fields
  };
}

function normalizeRegistrationSubmission(raw: Record<string, unknown>): RegistrationSubmission {
  const answers = Array.isArray(raw.answers)
    ? raw.answers.map((item) => {
        const answer = item as Record<string, unknown>;
        const value = answer.value;
        return {
          key: String(answer.key ?? ''),
          value:
            typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
              ? value
              : String(value ?? '')
        };
      })
    : [];

  return {
    id: toId(raw._id ?? raw.id),
    formId: toId(raw.formId),
    schoolId: toId(raw.schoolId),
    answers,
    status: raw.status as RegistrationSubmissionStatus,
    applicantLabel: String(raw.applicantLabel ?? 'Applicant'),
    reviewNote: typeof raw.reviewNote === 'string' ? raw.reviewNote : null,
    reviewedBy: raw.reviewedBy ? toId(raw.reviewedBy) : null,
    reviewedAt: typeof raw.reviewedAt === 'string' ? raw.reviewedAt : null,
    assignedClassId: raw.assignedClassId ? toId(raw.assignedClassId) : null,
    studentUserId: raw.studentUserId ? toId(raw.studentUserId) : null,
    isActive: Boolean(raw.isActive),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : ''
  };
}

function publicJsonHeaders(): HeadersInit {
  return { 'Content-Type': 'application/json' };
}

export async function getPublicRegistrationForm(formId: string): Promise<PublicRegistrationForm> {
  const response = await fetch(`${API_BASE_URL}/registration-forms/${formId}/public`, {
    headers: publicJsonHeaders()
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizePublicRegistrationForm(data);
}

export async function submitRegistration(
  body: SubmitRegistrationInput
): Promise<RegistrationSubmission> {
  const response = await fetch(`${API_BASE_URL}/registration-submissions`, {
    method: 'POST',
    headers: publicJsonHeaders(),
    body: JSON.stringify(body)
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeRegistrationSubmission(data);
}

export async function listRegistrationSubmissions(
  schoolId: string,
  filters?: {
    formId?: string;
    status?: RegistrationSubmissionStatus | '';
    page?: number;
    limit?: number;
  }
): Promise<RegistrationSubmission[]> {
  const query = buildQuery({
    formId: filters?.formId || undefined,
    status: filters?.status || undefined,
    page: filters?.page ?? 1,
    limit: filters?.limit ?? 100
  });
  const response = await fetch(
    `${API_BASE_URL}/schools/${schoolId}/registration-submissions${query}`,
    { headers: authHeaders() }
  );
  const data = await parseResponse<{
    items: Array<Record<string, unknown>>;
    total: number;
    page: number;
    limit: number;
  }>(response);
  return data.items.map(normalizeRegistrationSubmission);
}

export async function acceptRegistrationSubmission(
  schoolId: string,
  submissionId: string,
  reviewNote?: string
): Promise<RegistrationSubmission> {
  const response = await fetch(
    `${API_BASE_URL}/schools/${schoolId}/registration-submissions/${submissionId}/accept`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(reviewNote ? { reviewNote } : {})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeRegistrationSubmission(data);
}

export async function clearRegistrationInterview(
  schoolId: string,
  submissionId: string,
  reviewNote?: string
): Promise<RegistrationSubmission> {
  const response = await fetch(
    `${API_BASE_URL}/schools/${schoolId}/registration-submissions/${submissionId}/clear-interview`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(reviewNote ? { reviewNote } : {})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeRegistrationSubmission(data);
}

export async function rejectRegistrationSubmission(
  schoolId: string,
  submissionId: string,
  reviewNote?: string
): Promise<RegistrationSubmission> {
  const response = await fetch(
    `${API_BASE_URL}/schools/${schoolId}/registration-submissions/${submissionId}/reject`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(reviewNote ? { reviewNote } : {})
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeRegistrationSubmission(data);
}

export async function assignRegistrationClass(
  schoolId: string,
  submissionId: string,
  body: AssignRegistrationClassInput
): Promise<RegistrationSubmission> {
  const response = await fetch(
    `${API_BASE_URL}/schools/${schoolId}/registration-submissions/${submissionId}/assign-class`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body)
    }
  );
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeRegistrationSubmission(data);
}

function normalizeBadge(raw: Record<string, unknown>): Badge {
  return {
    id: toId(raw._id ?? raw.id),
    name: String(raw.name ?? ''),
    description: optionalText(raw.description),
    icon: typeof raw.icon === 'string' ? raw.icon.trim() : '',
    isActive: Boolean(raw.isActive),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : ''
  };
}

export async function listBadges(filters?: { activeOnly?: boolean }): Promise<Badge[]> {
  const query = buildQuery({
    activeOnly: filters?.activeOnly === true ? 'true' : undefined
  });
  const response = await fetch(`${API_BASE_URL}/badges${query}`, {
    headers: authHeaders()
  });
  const data = await parseResponse<Array<Record<string, unknown>>>(response);
  return data.map(normalizeBadge);
}

export async function getBadge(badgeId: string): Promise<Badge> {
  const response = await fetch(`${API_BASE_URL}/badges/${badgeId}`, {
    headers: authHeaders()
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeBadge(data);
}

export async function createBadge(body: CreateBadgeInput): Promise<Badge> {
  const response = await fetch(`${API_BASE_URL}/badges`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeBadge(data);
}

export async function updateBadge(badgeId: string, body: UpdateBadgeInput): Promise<Badge> {
  const response = await fetch(`${API_BASE_URL}/badges/${badgeId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeBadge(data);
}

export async function deactivateBadge(badgeId: string): Promise<Badge> {
  const response = await fetch(`${API_BASE_URL}/badges/${badgeId}/deactivate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({})
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeBadge(data);
}

export async function reactivateBadge(badgeId: string): Promise<Badge> {
  const response = await fetch(`${API_BASE_URL}/badges/${badgeId}/reactivate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({})
  });
  const data = await parseResponse<Record<string, unknown>>(response);
  return normalizeBadge(data);
}
